"use client";

import { useEffect } from "react";
import {
  calculateReadingProgress,
  getReadingCompletionThresholdSeconds,
  normalizeReadingProgressBucket
} from "@/lib/reading-engagement";
import { estimateProfileReadingStats } from "@/lib/reading-time";
import type { ProfileContent } from "@/lib/types";

export function ProfileReadingTracker({ profile, enabled = true }: { profile: ProfileContent; enabled?: boolean }) {
  const estimatedSeconds = estimateProfileReadingStats(profile).seconds;
  const completionThresholdSeconds = getReadingCompletionThresholdSeconds(estimatedSeconds);

  useEffect(() => {
    if (!enabled) return;

    let activeMilliseconds = 0;
    let lastTick = performance.now();
    let lastSentSeconds = 0;
    let lastSentBucket = 0;
    let stopped = false;

    const updateClock = () => {
      const now = performance.now();
      const elapsed = Math.min(1500, Math.max(0, now - lastTick));
      if (document.visibilityState === "visible" && document.hasFocus()) activeMilliseconds += elapsed;
      lastTick = now;
    };

    const currentSample = () => {
      updateClock();
      const activeSeconds = Math.min(600, Math.floor(activeMilliseconds / 1000));
      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body?.scrollHeight ?? 0,
        window.innerHeight
      );
      const progressPercent = calculateReadingProgress({
        activeSeconds,
        completionThresholdSeconds,
        viewportBottom: window.scrollY + window.innerHeight,
        documentHeight
      });
      const bucket = normalizeReadingProgressBucket(progressPercent);
      return { activeSeconds, progressPercent, bucket };
    };

    const send = (preferBeacon = false) => {
      if (stopped && !preferBeacon) return;
      const sample = currentSample();
      if (sample.activeSeconds <= lastSentSeconds && sample.bucket <= lastSentBucket) return;
      const payload = JSON.stringify({
        profileId: profile.id,
        handle: profile.handle,
        activeSeconds: sample.activeSeconds,
        progressPercent: sample.progressPercent
      });
      lastSentSeconds = sample.activeSeconds;
      lastSentBucket = sample.bucket;

      if (preferBeacon && navigator.sendBeacon) {
        navigator.sendBeacon("/api/metrics/reading", new Blob([payload], { type: "application/json" }));
        return;
      }
      void fetch("/api/metrics/reading", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        keepalive: true
      }).catch(() => undefined);
    };

    const tick = window.setInterval(() => {
      const sample = currentSample();
      if (sample.bucket > lastSentBucket || sample.activeSeconds - lastSentSeconds >= 10) send();
    }, 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") send(true);
      else lastTick = performance.now();
    };
    const onPageHide = () => send(true);
    const onFocusChange = () => {
      updateClock();
      lastTick = performance.now();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocusChange);
    window.addEventListener("blur", onFocusChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      stopped = true;
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocusChange);
      window.removeEventListener("blur", onFocusChange);
      window.removeEventListener("pagehide", onPageHide);
      send(true);
    };
  }, [completionThresholdSeconds, enabled, profile.handle, profile.id]);

  return null;
}
